import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { daysSince } from "@/features/servers/components/software-inventory-report/csv";
import {
  EmptyBlock,
  LoadingBlock,
} from "@/features/servers/components/software-inventory-report/ui";
import {
  OS_SUPPORT_STATUS_LABELS,
  type IHostVerificationRow,
  type ISoftwareInventoryRow,
} from "@/features/servers/schemas/servers";
import { isPastRevisionWindow } from "@/features/servers/services/infraSettingsService";
import { formatOsLabel } from "@/features/servers/utils/os-support";
import { isVersionOutdated } from "@/features/servers/utils/semver";

export function SoftwareTable({
  loading,
  rows,
  intervalMonths,
}: {
  loading: boolean;
  rows: ISoftwareInventoryRow[];
  intervalMonths: number;
}) {
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) {
    return (
      <EmptyBlock message="No hay datos de software en fichas de infraestructura." />
    );
  }
  return (
    <div className="rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2.5 font-medium">Centro</th>
            <th className="px-3 py-2.5 font-medium">Software</th>
            <th className="px-3 py-2.5 font-medium">Versión</th>
            <th className="px-3 py-2.5 font-medium">Objetivo</th>
            <th className="px-3 py-2.5 font-medium">Verificado</th>
            <th className="px-3 py-2.5 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pendingVerify = isPastRevisionWindow(
              r.verifiedAt,
              intervalMonths
            );
            const outdated = isVersionOutdated(r.version, r.targetVersion);
            const days = daysSince(r.verifiedAt);
            return (
              <tr
                key={`${r.institutionUid}-${r.instanceId}`}
                className="border-t hover:bg-muted/30 transition-colors"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/servers/${r.institutionUid}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {r.institutionName}
                  </Link>
                  {r.institutionEmail && (
                    <p className="text-xs text-muted-foreground">
                      {r.institutionEmail}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2">{r.softwareLabel}</td>
                <td className="px-3 py-2">
                  {r.version ? (
                    <span
                      className={`font-mono ${outdated ? "text-amber-700 dark:text-amber-400" : ""}`}
                    >
                      {r.version}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-muted-foreground">
                  {r.targetVersion || "—"}
                </td>
                <td className="px-3 py-2">
                  {r.verifiedAt ? (
                    <span
                      className={pendingVerify ? "text-amber-700 dark:text-amber-400" : undefined}
                    >
                      {r.verifiedAt}
                      {days !== null && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({days}d)
                        </span>
                      )}
                      {r.verifiedBy && (
                        <p className="text-xs text-muted-foreground">
                          {r.verifiedBy}
                        </p>
                      )}
                    </span>
                  ) : (
                    <Badge variant="secondary">Nunca</Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {outdated && (
                      <Badge variant="destructive">Desactualizado</Badge>
                    )}
                    <Badge variant={pendingVerify ? "secondary" : "default"}>
                      {pendingVerify ? "Verif. pendiente" : "Verif. al día"}
                    </Badge>
                    {!r.active && (
                      <Badge variant="outline">Inactivo</Badge>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HostsTable({
  loading,
  rows,
  intervalMonths,
}: {
  loading: boolean;
  rows: IHostVerificationRow[];
  intervalMonths: number;
}) {
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) {
    return (
      <EmptyBlock message="No hay hosts registrados en fichas de infraestructura." />
    );
  }
  return (
    <div className="rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">Centro</th>
            <th className="px-3 py-2 font-medium">Host</th>
            <th className="px-3 py-2 font-medium">SO</th>
            <th className="px-3 py-2 font-medium">Soporte</th>
            <th className="px-3 py-2 font-medium">Updates</th>
            <th className="px-3 py-2 font-medium">AnyDesk</th>
            <th className="px-3 py-2 font-medium">Verificado</th>
            <th className="px-3 py-2 font-medium">Estado verif.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pending = isPastRevisionWindow(r.verifiedAt, intervalMonths);
            const days = daysSince(r.verifiedAt);
            const osLabel =
              formatOsLabel(r.osFamily, r.osVersion, r.os) || r.os || "—";
            const needsAttention = r.osSupportStatus === "unsupported";
            return (
              <tr
                key={`${r.institutionUid}-${r.hostId}`}
                className="border-t hover:bg-muted/30"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/servers/${r.institutionUid}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {r.institutionName}
                  </Link>
                  {r.institutionEmail && (
                    <p className="text-xs text-muted-foreground">
                      {r.institutionEmail}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2">
                  <p className="font-medium">{r.hostName}</p>
                  {r.hostIp && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {r.hostIp}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2">
                  <p className={needsAttention ? "text-amber-700 dark:text-amber-400" : undefined}>
                    {osLabel}
                  </p>
                  {r.osVersion && (
                    <p className="text-xs font-mono text-muted-foreground">
                      {r.osVersion}
                      {r.recommendedOsVersion
                        ? ` · rec. ${r.recommendedOsVersion}`
                        : ""}
                      {r.minSupportedOsVersion
                        ? ` · mín. ${r.minSupportedOsVersion}`
                        : ""}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge
                    variant={
                      r.osSupportStatus === "recommended"
                        ? "default"
                        : r.osSupportStatus === "supported"
                          ? "outline"
                          : r.osSupportStatus === "unsupported"
                            ? "destructive"
                            : "secondary"
                    }
                  >
                    {OS_SUPPORT_STATUS_LABELS[r.osSupportStatus]}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {r.acceptsSoftwareUpdates ? (
                    <span className="text-emerald-700 dark:text-emerald-400 text-xs">
                      Sí
                    </span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400 text-xs font-medium">
                      No — actualizar SO
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.anydeskId || "—"}
                </td>
                <td className="px-3 py-2">
                  {r.verifiedAt ? (
                    <span className={pending ? "text-amber-700 dark:text-amber-400" : undefined}>
                      {r.verifiedAt}
                      {days !== null && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({days}d)
                        </span>
                      )}
                      {r.verifiedBy && (
                        <p className="text-xs text-muted-foreground">
                          {r.verifiedBy}
                        </p>
                      )}
                    </span>
                  ) : (
                    <Badge variant="secondary">Nunca</Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={pending ? "secondary" : "default"}>
                    {pending ? "Pendiente" : "Al día"}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
